/**
 * storageService.js
 *
 * S3-presigned-upload helpers for listing photos and inspection media —
 * same pattern as verificationApi.js (getS3UploadUrl Lambda + presigned POST).
 * There is no Firebase Storage in this app; all file storage is S3.
 */
import { apiFetch } from "./apiFetch";

const API_BASE_URL = import.meta.env.VITE_APP_API_URL || "https://auth.ezron.ai";

async function getUploadUrl(filename) {
  const url = `${API_BASE_URL}/getS3UploadUrl?filename=${encodeURIComponent(filename)}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getS3UploadUrl returned ${res.status}`);
  }
  const data = await res.json();
  return { postUrl: data.postUrl, fields: data.fields, key: data.fields.key };
}

async function uploadToS3(postUrl, fields, file) {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => form.append(k, v));
  form.append("file", file);

  const res = await fetch(postUrl, { method: "POST", body: form });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const codeMatch = text.match(/<Code>(.+?)<\/Code>/);
    throw new Error(codeMatch ? codeMatch[1] : `S3 upload failed with status ${res.status}`);
  }
}

/**
 * Uploads a listing photo and returns the metadata to store on the listing doc.
 * Returns { name, key, type, size }.
 */
export async function uploadListingImage(listingId, file) {
  const timestamp = Date.now();
  const safeName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  const filename = `listings/${listingId}/images/${safeName}`;
  const { postUrl, fields, key } = await getUploadUrl(filename);
  await uploadToS3(postUrl, fields, file);
  return { name: file.name, key, type: file.type, size: file.size };
}

/**
 * Resolves a listing image's S3 key to a short-lived, public presigned GET
 * URL (listing photos are shown on the public rental application page).
 */
export async function getListingImageUrl(key) {
  const url = `${API_BASE_URL}/getPublicListingImages?key=${encodeURIComponent(key)}`;
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getPublicListingImages returned ${res.status}`);
  }
  const data = await res.json();
  return data.url;
}

/**
 * Uploads a photo or video attached to a property inspection.
 * Returns { name, storagePath, type, size } — storagePath is the S3 key.
 */
export async function uploadInspectionMedia(inspectionId, file) {
  const timestamp = Date.now();
  const safeName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  const filename = `inspections/${inspectionId}/media/${safeName}`;
  const { postUrl, fields, key } = await getUploadUrl(filename);
  await uploadToS3(postUrl, fields, file);
  return { name: file.name, storagePath: key, type: file.type, size: file.size };
}

/**
 * Resolves an inspection media S3 key to a short-lived presigned GET URL.
 * Authorized server-side: only property participants may view it.
 */
export async function getInspectionMediaUrl(inspectionId, storagePath) {
  const url = `${API_BASE_URL}/getInspectionMediaUrl?inspectionId=${encodeURIComponent(inspectionId)}&key=${encodeURIComponent(storagePath)}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getInspectionMediaUrl returned ${res.status}`);
  }
  const data = await res.json();
  return data.url;
}

/** Deletes an inspection's media file from S3. Call before removing it from Firestore. */
export async function deleteInspectionMedia(inspectionId, storagePath) {
  const url = `${API_BASE_URL}/deleteInspectionMedia?inspectionId=${encodeURIComponent(inspectionId)}&key=${encodeURIComponent(storagePath)}`;
  const res = await apiFetch(url, { method: "DELETE" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `deleteInspectionMedia returned ${res.status}`);
  }
}
